// Pure rules for the Company Settings page: no Vue, no API calls, so they are easy to test.

export const INDIAN_STATES = [
  { code: "01", name: "Jammu and Kashmir" },
  { code: "02", name: "Himachal Pradesh" },
  { code: "03", name: "Punjab" },
  { code: "04", name: "Chandigarh" },
  { code: "05", name: "Uttarakhand" },
  { code: "06", name: "Haryana" },
  { code: "07", name: "Delhi" },
  { code: "08", name: "Rajasthan" },
  { code: "09", name: "Uttar Pradesh" },
  { code: "10", name: "Bihar" },
  { code: "11", name: "Sikkim" },
  { code: "12", name: "Arunachal Pradesh" },
  { code: "13", name: "Nagaland" },
  { code: "14", name: "Manipur" },
  { code: "15", name: "Mizoram" },
  { code: "16", name: "Tripura" },
  { code: "17", name: "Meghalaya" },
  { code: "18", name: "Assam" },
  { code: "19", name: "West Bengal" },
  { code: "20", name: "Jharkhand" },
  { code: "21", name: "Odisha" },
  { code: "22", name: "Chhattisgarh" },
  { code: "23", name: "Madhya Pradesh" },
  { code: "24", name: "Gujarat" },
  { code: "25", name: "Daman and Diu" },
  { code: "26", name: "Dadra and Nagar Haveli" },
  { code: "27", name: "Maharashtra" },
  { code: "28", name: "Andhra Pradesh" },
  { code: "29", name: "Karnataka" },
  { code: "30", name: "Goa" },
  { code: "31", name: "Lakshadweep" },
  { code: "32", name: "Kerala" },
  { code: "33", name: "Tamil Nadu" },
  { code: "34", name: "Puducherry" },
  { code: "35", name: "Andaman and Nicobar Islands" },
  { code: "36", name: "Telangana" },
  { code: "37", name: "Ladakh" },
];

export const GST_TYPES = [
  { value: "REGULAR", label: "Regular", description: "Registered under GST and charges GST on invoices." },
  { value: "COMPOSITION", label: "Composition", description: "Registered under the composition scheme (bill of supply)." },
  { value: "UNREGISTERED", label: "Unregistered", description: "Not registered under GST. No GSTIN needed." },
];

export const LOCATION_TYPES = [
  { value: "STORE", label: "Store" },
  { value: "WAREHOUSE", label: "Warehouse" },
  { value: "GODOWN", label: "Godown" },
  { value: "COUNTER", label: "Counter" },
];

// Tabs of the page and the fields each one owns (used for error badges and "jump to field").
export const SECTIONS = [
  {
    key: "business",
    label: "Business",
    icon: "briefcase",
    fields: ["CompanyName", "DisplayName", "AddressLine1", "AddressLine2", "City", "State", "StateCode", "PinCode", "Phone1", "Email"],
  },
  { key: "gst", label: "GST & registration", icon: "file-text", fields: ["GSTType", "GSTIN", "PANNo", "DrugLicenseNo"] },
  { key: "bank", label: "Bank & payments", icon: "credit-card", fields: ["BankName", "AccountNo", "IFSCCode", "UPIId"] },
  { key: "invoice", label: "Invoice", icon: "printer", fields: ["InvoicePrefix", "PurchasePrefix", "InvoiceFooter", "InvoiceTerms"] },
  { key: "locations", label: "Locations", icon: "map-pin", fields: [] },
];

// Fields this page may send to PUT /v1/company-settings. Location limits and mode are
// managed by the platform admin, and the logo has its own endpoint.
export const EDITABLE_FIELDS = SECTIONS.flatMap((section) => section.fields);

export const FIELD_LABELS = {
  CompanyName: "Company name",
  DisplayName: "Display name",
  AddressLine1: "Address line 1",
  AddressLine2: "Address line 2",
  City: "City",
  State: "State",
  StateCode: "State code",
  PinCode: "PIN code",
  Phone1: "Phone",
  Email: "Email",
  GSTType: "GST type",
  GSTIN: "GSTIN",
  PANNo: "PAN",
  DrugLicenseNo: "Drug licence number",
  BankName: "Bank name",
  AccountNo: "Account number",
  IFSCCode: "IFSC code",
  UPIId: "UPI ID",
  InvoicePrefix: "Sales invoice prefix",
  PurchasePrefix: "Purchase prefix",
  InvoiceFooter: "Invoice footer",
  InvoiceTerms: "Terms & conditions",
};

// Typed in lower case by users but always stored in upper case.
export const UPPERCASE_FIELDS = ["GSTIN", "PANNo", "IFSCCode", "InvoicePrefix", "PurchasePrefix"];

export const DEFAULT_FORM = Object.freeze({
  CompanyName: "",
  DisplayName: "",
  Logo: "",
  LogoUrl: "",
  AddressLine1: "",
  AddressLine2: "",
  City: "",
  State: "",
  StateCode: "",
  PinCode: "",
  Phone1: "",
  Email: "",
  GSTIN: "",
  GSTType: "REGULAR",
  PANNo: "",
  DrugLicenseNo: "",
  BankName: "",
  AccountNo: "",
  IFSCCode: "",
  UPIId: "",
  InvoicePrefix: "INV",
  PurchasePrefix: "PUR",
  InvoiceFooter: "",
  InvoiceTerms: "",
  MultiLocationEnabled: false,
  MaxLocations: 1,
  DefaultLocationId: "",
  LocationMode: "SINGLE",
});

const PATTERNS = {
  GSTIN: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
  PAN: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
  IFSC: /^[A-Z]{4}0[A-Z0-9]{6}$/,
  PIN: /^[1-9][0-9]{5}$/,
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  UPI: /^[A-Za-z0-9.\-_]{2,}@[A-Za-z]{2,}$/,
  PREFIX: /^[A-Z0-9-]{1,10}$/,
  ACCOUNT: /^[0-9]{9,18}$/,
};

const text = (value) => (value === null || value === undefined ? "" : String(value).trim());
const digits = (value) => text(value).replace(/\D/g, "");

export function stateByCode(code) {
  return INDIAN_STATES.find((state) => state.code === text(code)) || null;
}

export function stateByName(name) {
  const wanted = text(name).toLowerCase();
  return INDIAN_STATES.find((state) => state.name.toLowerCase() === wanted) || null;
}

/** State and PAN that a valid-looking GSTIN implies (both null when it cannot tell). */
export function detailsFromGstin(gstin) {
  const value = text(gstin).toUpperCase();
  if (value.length < 12) return { state: null, pan: null };
  return {
    state: stateByCode(value.slice(0, 2)),
    pan: PATTERNS.PAN.test(value.slice(2, 12)) ? value.slice(2, 12) : null,
  };
}

// Each validator returns an error message or null. They receive the whole form for cross-field rules.
const required = (label) => (value) => (text(value) ? null : `${label} is required`);

export const VALIDATORS = {
  CompanyName: required("Company name"),
  AddressLine1: required("Address"),
  City: required("City"),
  State: required("State"),
  StateCode: (value) => (stateByCode(value) ? null : "Choose the state to set its code"),
  PinCode: (value) => {
    if (!text(value)) return "PIN code is required";
    return PATTERNS.PIN.test(text(value)) ? null : "PIN code must be 6 digits";
  },
  Phone1: (value) => {
    if (!text(value)) return "Phone is required";
    const count = digits(value).length;
    return count >= 10 && count <= 13 ? null : "Enter a valid phone number";
  },
  Email: (value) => (!text(value) || PATTERNS.EMAIL.test(text(value)) ? null : "Enter a valid email address"),
  GSTIN: (value, form) => {
    const gstin = text(value).toUpperCase();
    if (!gstin) return form.GSTType === "UNREGISTERED" ? null : "GSTIN is required for GST-registered businesses";
    if (!PATTERNS.GSTIN.test(gstin)) return "GSTIN should look like 32ABCDE1234F1Z5 (15 characters)";
    const state = stateByCode(gstin.slice(0, 2));
    if (!state) return `GSTIN state code ${gstin.slice(0, 2)} is not valid`;
    if (text(form.StateCode) && text(form.StateCode) !== state.code) {
      return `GSTIN is from ${state.name}, but the address state is ${form.State || form.StateCode}`;
    }
    return null;
  },
  PANNo: (value, form) => {
    const pan = text(value).toUpperCase();
    if (!pan) return null;
    if (!PATTERNS.PAN.test(pan)) return "PAN should look like ABCDE1234F";
    const fromGstin = detailsFromGstin(form.GSTIN).pan;
    return fromGstin && fromGstin !== pan ? `PAN does not match the GSTIN (expected ${fromGstin})` : null;
  },
  AccountNo: (value, form) => {
    if (!text(value)) return text(form.IFSCCode) || text(form.BankName) ? "Add the account number too" : null;
    return PATTERNS.ACCOUNT.test(digits(value)) && digits(value) === text(value) ? null : "Account number must be 9–18 digits";
  },
  IFSCCode: (value, form) => {
    if (!text(value)) return text(form.AccountNo) ? "Add the IFSC code for this account" : null;
    return PATTERNS.IFSC.test(text(value).toUpperCase()) ? null : "IFSC should look like SBIN0001234";
  },
  UPIId: (value) => (!text(value) || PATTERNS.UPI.test(text(value)) ? null : "UPI ID should look like name@bank"),
  InvoicePrefix: (value) => {
    if (!text(value)) return "Sales invoice prefix is required";
    return PATTERNS.PREFIX.test(text(value).toUpperCase()) ? null : "Use up to 10 letters, numbers or '-'";
  },
  PurchasePrefix: (value) => {
    if (!text(value)) return "Purchase prefix is required";
    return PATTERNS.PREFIX.test(text(value).toUpperCase()) ? null : "Use up to 10 letters, numbers or '-'";
  },
};

export const REQUIRED_FIELDS = ["CompanyName", "AddressLine1", "City", "State", "PinCode", "Phone1", "InvoicePrefix", "PurchasePrefix"];

export function isRequired(field, form) {
  if (field === "GSTIN") return form.GSTType !== "UNREGISTERED";
  return REQUIRED_FIELDS.includes(field);
}

/** { field: message } for every invalid field. */
export function validateForm(form) {
  const errors = {};
  for (const [field, validate] of Object.entries(VALIDATORS)) {
    const message = validate(form[field], form);
    if (message) errors[field] = message;
  }
  return errors;
}

export function sectionOfField(field) {
  return SECTIONS.find((section) => section.fields.includes(field))?.key || null;
}

export function errorCountBySection(errors) {
  const counts = {};
  for (const field of Object.keys(errors)) {
    const section = sectionOfField(field);
    if (section) counts[section] = (counts[section] || 0) + 1;
  }
  return counts;
}

// Optional details that make invoices complete; shown as a setup checklist.
export const RECOMMENDED_FIELDS = [
  { field: "LogoUrl", label: "Add your logo", section: "business" },
  { field: "Email", label: "Add a contact email", section: "business" },
  { field: "GSTIN", label: "Add your GSTIN", section: "gst", when: (form) => form.GSTType !== "UNREGISTERED" },
  { field: "PANNo", label: "Add your PAN", section: "gst" },
  { field: "AccountNo", label: "Add bank details for invoices", section: "bank" },
  { field: "UPIId", label: "Add a UPI ID for payments", section: "bank" },
  { field: "InvoiceTerms", label: "Add invoice terms & conditions", section: "invoice" },
];

/** Setup checklist: required fields must be valid, recommended fields filled. */
export function setupProgress(form, errors = validateForm(form)) {
  const requiredDone = REQUIRED_FIELDS.filter((field) => !errors[field]).length + (errors.GSTIN ? 0 : 1);
  const requiredTotal = REQUIRED_FIELDS.length + 1;
  const recommended = RECOMMENDED_FIELDS.filter((item) => !item.when || item.when(form));
  const missing = recommended.filter((item) => !text(form[item.field]));
  const done = requiredDone + (recommended.length - missing.length);
  const total = requiredTotal + recommended.length;
  return { percent: Math.round((done * 100) / total), missing, requiredComplete: requiredDone === requiredTotal };
}

export function financialYearLabel(onDate = new Date()) {
  const start = onDate.getMonth() >= 3 ? onDate.getFullYear() : onDate.getFullYear() - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
}

/** Mirrors BillNumberSeries in the API: PREFIX/2026-27/09/000001 */
export function billNumberPreview(prefix, onDate = new Date(), sequence = 1) {
  const month = String(onDate.getMonth() + 1).padStart(2, "0");
  return `${text(prefix).toUpperCase() || "PREFIX"}/${financialYearLabel(onDate)}/${month}/${String(sequence).padStart(6, "0")}`;
}

/** Tidy user input before validating or saving. */
export function normalizeField(field, value) {
  if (typeof value !== "string") return value;
  if (UPPERCASE_FIELDS.includes(field)) return value.toUpperCase().replace(/\s+/g, "");
  return value;
}

/** Every editable field, trimmed and normalized. */
export function editableValues(form) {
  const values = {};
  for (const field of EDITABLE_FIELDS) {
    let value = form[field];
    if (typeof value === "string") value = normalizeField(field, value.trim());
    values[field] = value ?? "";
  }
  return values;
}

/** Body for PUT /v1/company-settings. An empty email is left out because the API rejects "". */
export function buildPayload(form) {
  const payload = editableValues(form);
  if (!payload.Email) delete payload.Email;
  return payload;
}

export function snapshot(form) {
  return JSON.stringify(editableValues(form));
}

/** Location IDs are generated from the name: "Main Store" -> "main-store" (unique among existing IDs). */
export function locationIdFromName(name, existingIds = []) {
  const base =
    text(name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "location";
  const taken = new Set(existingIds.map((id) => String(id).toLowerCase()));
  if (!taken.has(base)) return base;
  for (let n = 2; ; n += 1) {
    const candidate = `${base}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}
